import { yupResolver } from "@hookform/resolvers/yup"
import type { TFunction } from "i18next"
import React, { createContext, useContext, useState } from "react"

import { isValidPhoneNumber } from "@constants/validation"
import { useAppContext } from "@contexts/app_context"
import { getWhatsappApiPlatform } from "@domain/whatsappApi"
import useControlledForm from "@hooks/use_controlled_form"
import useToggle from "@hooks/use_toggle"
import { useQuery } from "@tanstack/react-query"
import i18next from "@utils/localization"
import type {
  Control,
  UseFormHandleSubmit,
  UseFormReset
} from "react-hook-form"
import * as yup from "yup"
import { User } from "__generated__/graphql"

export type WhatsappApiResponse = {
  _id: string
  name: string
  channelId: string
  status: boolean
  appName: string
  appId: string
  apiKey: string
  appToken: string
  customer: { _id: string; companyName: string }
  product_template_id: string
  createdAt: string
}

export type WhatsappFormData = {
  company: string
  displayName: string
  phone: {
    value: string
    dialCode?: string
    countryCode: string
  }
  status: boolean
  appName?: string
  appID?: string
  appKey?: string
  appToken?: string
  productTemplate?: string
}

type WhatsappPageContextProps = {
  translate: TFunction<"translation", undefined>
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  whatsappData?: WhatsappApiResponse[]
  control: Control<WhatsappFormData>
  handleSubmit?: UseFormHandleSubmit<WhatsappFormData>
  isValid?: boolean
  isDirty?: boolean
  reset?: UseFormReset<WhatsappFormData>
  me?: User
  isLoading: boolean
  pagination?: {
    page: number
    pageSize: number
  }
  setPagination?: React.Dispatch<
    React.SetStateAction<{
      page: number
      pageSize: number
    }>
  >
  search?: string
  setSearch: (search: string) => void
}

const defaultValues: WhatsappPageContextProps = {
  translate: i18next.t,
  control: {} as Control<WhatsappFormData>,
  isLoading: true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSearch: (search: string) => {}
}

const WhatsappPageContext =
  createContext<WhatsappPageContextProps>(defaultValues)

export function WhatsappPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate, me } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })
  const [search, setSearch] = useState("")

  const { data: whatsappData, isLoading } = useQuery({
    queryKey: ["whatsapp-data-table", pagination, search],
    queryFn: async () => {
      const response = await getWhatsappApiPlatform({
        search
      })
      return response.data?.data as unknown as WhatsappApiResponse[]
    }
  })

  const schema = yup
    .object({
      company: yup
        .string()
        .required(translate("company_name_field_is_required")),
      displayName: yup
        .string()
        .required(translate("display_name_field_is_required")),
      appID: yup.string(),
      status: yup.boolean().required(),
      appName: yup.string(),
      appToken: yup.string(),
      appKey: yup.string(),
      productTemplate: yup.string(),
      phone: yup
        .object()
        .shape({
          value: yup.string().required(translate("phone_number_is_not_valid")),
          dialCode: yup.string(),
          countryCode: yup
            .string()
            .required(translate("phone_number_is_not_valid"))
        })
        .test(
          "phone_number_is_not_valid",
          translate("phone_number_is_not_valid"),
          isValidPhoneNumber
        )
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useControlledForm<WhatsappFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  return (
    <WhatsappPageContext.Provider
      value={{
        ...defaultValues,
        translate,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        control,
        handleSubmit,
        isValid,
        isDirty,
        reset,
        me,
        isLoading,
        search,
        setPagination,
        setSearch,
        pagination,
        whatsappData
      }}
    >
      {props.children}
    </WhatsappPageContext.Provider>
  )
}

export function useWhatsappPageContext() {
  const context = useContext(WhatsappPageContext)
  if (context === undefined) {
    throw new Error(
      "WhatsappPageContext must be used within a WhatsappPageContextProvider"
    )
  }
  return context
}
